<template>
    <Page behind-nav>
        <!-- Introduction -->
        <ProAnchor name="introduction">
            <section class="procreate-hero procreate-hero--fade-out procreate-hero--content-bottom">
                <div class="procreate-hero__media">
                    <img class="rmnp-hidden" :src="$asset($t('ipad-color.banner.video_poster'))" />
                    <video
                        class="rm-hidden"
                        :src="$asset($t('ipad-color.banner.video'))"
                        :poster="$asset($t('ipad-color.banner.video_poster'))"
                        playsinline
                        muted
                        autoplay
                        loop
                    ></video>
                    <CreditMedia
                        :credit-only="true"
                        artist="Jaye Kang"
                        artwork="Xuanwu 4000"
                        :commission="true"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/jaye-kang-xuanwu.jpg')"
                    />
                    <CreditMedia
                        :credit-only="true"
                        artist="Rebecca Mills"
                        artwork="Little Help!"
                        :commission="false"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/rebecca-mills-little-help.jpg')"
                    />
                    <CreditMedia
                        :credit-only="true"
                        artist="Even Amundsen"
                        artwork="The Deepwoods"
                        :commission="true"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/even-amundsen-the_deepwoods.jpg')"
                    />
                </div>

                <div class="container">
                    <div class="procreate-hero__content">
                        <div class="procreate-copy procreate-copy--center procreate-copy--bright">
                            <h1 class="text-subheading">{{ $t("ipad-color.banner.subheading") }}</h1>
                            <p class="text-heading text-heading--md">{{ $t("ipad-color.banner.heading") }}</p>
                            <p class="text-body" v-html="$t('ipad-color.banner.body')"></p>
                        </div>
                    </div>
                </div>
            </section>
        </ProAnchor>

        <!-- Color Companion -->
        <ProAnchor name="color-companion">
            <section class="procreate-section procreate-section--narrow-top">
                <DualIpad :title="$t('ipad-color.color_companion.heading')" :body="$t('ipad-color.color_companion.body')">
                    <template v-slot:left>
                        <CreditMedia
                            :src="$asset($t('ipad-color.color_companion.image_left'))"
                            alt=""
                            width="1300"
                            height="1734"
                            artist="Jarom Vogel"
                            artwork="Monkey"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/jarom-vogel-monkey.jpg')"
                        />
                    </template>
                    <template v-slot:right>
                        <CreditMedia
                            :src="$asset($t('ipad-color.color_companion.image_right'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Christiane King"
                            artwork="Untitled Study"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/christiane-king-untitled.jpg')"
                        />
                    </template>
                </DualIpad>
            </section>
        </ProAnchor>

        <!-- Gradient Map -->
        <ProAnchor name="gradient-map">
            <div class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--center">
                        <h2 class="text-subheading">{{ $t("ipad-color.gradient_map.subheading") }}</h2>
                        <p class="text-heading text-heading--md">{{ $t("ipad-color.gradient_map.heading") }}</p>
                        <p class="text-body" v-html="$t('ipad-color.gradient_map.body')"></p>
                    </div>
                </div>
                <SingleIpad>
                    <template v-slot:ipad>
                        <img class="rmnp-hidden" :src="$asset($t('ipad-color.gradient_map.video_poster'))" />
                        <video
                            class="rm-hidden"
                            :src="$asset($t('ipad-color.gradient_map.video'))"
                            :poster="$asset($t('ipad-color.gradient_map.video_poster'))"
                            width="1734"
                            height="1300"
                            loop
                            playsinline
                            muted
                            autoplay
                        />
                        <CreditMedia
                            artist="Angela Sung"
                            artwork="Light and Dark"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/angela-sung-light-and-dark.jpg')"
                            credit-only
                        />
                    </template>
                </SingleIpad>
            </div>
        </ProAnchor>

        <!-- ColorDrop -->
        <ProAnchor name="colordrop">
            <section class="background-media background-media--tall background-media--fade-bottom background-media--bottom background-media--content-wider">
                <div class="background-media__media">
                    <img class="rmnp-hidden" :src="$asset($t('ipad-color.color_drop.video_poster'))" />
                    <video
                        class="rm-hidden"
                        :src="$asset($t('ipad-color.color_drop.video'))"
                        :poster="$asset($t('ipad-color.color_drop.video_poster'))"
                        playsinline
                        muted
                        autoplay
                        loop
                    ></video>

                    <CreditMedia
                        :credit-only="true"
                        artist="Will Robinson"
                        artwork="Rampage"
                        :commission="true"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/will-robinson-rampage.jpg')"
                    />
                    <CreditMedia
                        :credit-only="true"
                        artist="Ivan Belikov / Andrew Proctor"
                        artwork="Guitar"
                        :commission="true"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/ivan-belikov-guitar.jpg')"
                    />
                    <CreditMedia
                        :credit-only="true"
                        artist="Nick Liefhebber"
                        artwork="Wild Flower"
                        :commission="true"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/nick-liefhebber-wild-flower.jpg')"
                    />
                </div>

                <div class="container">
                    <div class="background-media__content">
                        <p
                            class="background-media__heading background-media__large-body text-heading text-heading--sm"
                            v-html="$t('ipad-color.color_drop.body')"
                        ></p>
                    </div>
                </div>
            </section>
        </ProAnchor>

        <!-- Color Balance -->
        <ProAnchor name="color-balance">
            <div class="procreate-section">
                <SingleIpad reverse>
                    <template v-slot:ipad>
                        <CreditMedia
                            :src="$asset($t('ipad-color.color-balance.image'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Tom Everingham"
                            artwork="Cut Out"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/tom-everingham-cut-out.jpg')"
                        />
                    </template>
                    <div class="procreate-copy procreate-copy--center-sm">
                        <h2 class="text-subheading">{{ $t("ipad-color.color-balance.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-color.color-balance.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-color.color-balance.body')"></p>
                    </div>
                </SingleIpad>
            </div>
        </ProAnchor>

        <!-- Palettes -->
        <ProAnchor name="palettes">
            <section class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--center">
                        <h2 class="text-subheading">{{ $t("ipad-color.palettes.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-color.palettes.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-color.palettes.body')"></p>
                    </div>
                </div>
                <SingleIpad>
                    <template v-slot:ipad>
                        <CreditMedia
                            :src="$asset($t('ipad-color.palettes.image'))"
                            alt=""
                            height="1300"
                            width="1734"
                            artist="Sam Rodriguez"
                            artwork="Red Cap"
                            :commission="false"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/sam-rodriquez-untitled-2.jpg')"
                        />
                    </template>

                    <ul class="feature-list">
                        <li>
                            <div class="feature-list__title text-heading text-heading--xs">
                                <div class="feature-list__icon">
                                    <ProIcon icon="color-description" />
                                </div>
                                <h3>{{ $t("ipad-color.palettes.description.heading") }}</h3>
                            </div>
                            <p class="feature-list__body text-body" v-html="$t('ipad-color.palettes.description.body')"></p>
                        </li>

                        <li>
                            <div class="feature-list__title text-heading text-heading--xs">
                                <div class="feature-list__icon">
                                    <ProIcon icon="palettes" />
                                </div>
                                <h3>{{ $t("ipad-color.palettes.larger.heading") }}</h3>
                            </div>
                            <p class="feature-list__body text-body" v-html="$t('ipad-color.palettes.larger.body')"></p>
                        </li>
                    </ul>
                </SingleIpad>
            </section>
        </ProAnchor>

        <!-- Color Dynamics -->
        <ProAnchor name="color-dynamics">
            <section class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--widest">
                        <h2 class="text-subheading">{{ $t("ipad-color.color_dynamics.heading") }}</h2>
                        <p class="text-heading" v-html="$t('ipad-color.color_dynamics.body')"></p>
                    </div>
                </div>
                <DualIpad overlap>
                    <template v-slot:left>
                        <img :src="$asset($t('ipad-color.color_dynamics.image_left'))" alt="" width="1734" height="1300" />
                    </template>
                    <template v-slot:right>
                        <CreditMedia
                            :src="$asset($t('ipad-color.color_dynamics.image_right'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Aaron Griffin"
                            artwork="Neon"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/aaron-griffin-neon.jpg')"
                        />
                    </template>
                </DualIpad>
            </section>
        </ProAnchor>

        <!-- Palette Capture -->
        <ProAnchor name="palette-capture">
            <section class="procreate-section">
                <ProcreateSlideshow :items="$t('ipad-color.palette_capture.slides')">
                    <div class="procreate-copy procreate-copy--center">
                        <p class="text-subheading">{{ $t("ipad-color.palette_capture.subheading") }}</p>
                        <h2 class="text-heading text-heading--md">{{ $t("ipad-color.palette_capture.heading") }}</h2>
                        <p class="text-body" v-html="$t('ipad-color.palette_capture.body')"></p>
                    </div>
                </ProcreateSlideshow>
            </section>
        </ProAnchor>

        <section class="procreate-section">
            <div class="container container--narrow" v-fade>
                <IpadBuyNow ctaLink="https://apps.apple.com/app/apple-store/id425073498?pt=345446&ct=Website_ipad-color&mt=8" />
            </div>
        </section>

        <ArtistCredits />
    </Page>
</template>

<script>
import { metaTitles, metaDescriptions, metaImages, metaKeywords } from "~/utils/meta";
import { procreateSoftwareApplication, createJsonLdWrapper } from "@/utils/schema.org.js";

import ProcreateSlideshow from "@/components/ipad/ProcreateSlideshow";
import DualIpad from "@/components/ipad/DualIpad";
import SingleIpad from "@/components/ipad/SingleIpad";
import IpadBuyNow from "@/components/ipad/IpadBuyNow";
import CreditMedia from "@/components/CreditMedia";
import ArtistCredits from "@/components/ArtistCredits";

export default {
    components: {
        ProcreateSlideshow,
        DualIpad,
        SingleIpad,
        IpadBuyNow,
        CreditMedia,
        ArtistCredits,
    },

    head() {
        const i18nSeo = this.$nuxtI18nSeo();

        return {
            title: this.$t("ipad-color.meta.title"),
            script: [createJsonLdWrapper(procreateSoftwareApplication)],
            meta: [
                ...metaTitles(this.$t("ipad-color.meta.title")),
                ...metaDescriptions(this.$t("ipad-color.meta.description")),
                ...metaImages(this.$asset(this.$t("ipad-color.meta.image"))),
                ...metaKeywords(this.$t("ipad-color.meta.keywords")),
                { hid: "apple-itunes-app", name: "apple-itunes-app", content: "app-id=425073498" },
                ...i18nSeo.meta,
            ],
            link: [...i18nSeo.link],
        };
    },
};
</script>
